import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  #observeIntersection = () => {
    this.#intersectionObserver?.disconnect();
    if (!this.isConnected) return;
    if (this.dataset.recommendationsPerformed === 'true' && this.#requestKey() === this.#renderedKey) return;

    // Horizon scrolls .page-wrapper above 990px. A viewport-root margin cannot
    // look past that nested scroller's clip, so use its shared root resolver.
    this.#intersectionObserver = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.#loadRecommendations();
      },
      { root: getIntersectionRoot(), rootMargin: '0px 0px 1000px 0px' }
    );
    this.#intersectionObserver.observe(this);
  };

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver(() => {
    // Presentation changes during hydration do not change the recommendation query.
    if (this.#requestKey() !== this.#renderedKey || this.dataset.recommendationsPerformed !== 'true') {
      this.#loadRecommendations();
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #renderedKey = '';
  #pendingKey = '';
  #requestVersion = 0;

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.recommendationsPerformed === 'true') {
      this.#renderedKey = this.#requestKey();
    }
    this.#observeIntersection();
    scrollContainerMediaQuery.addEventListener('change', this.#observeIntersection);
    this.#observeQueryChanges();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();
    scrollContainerMediaQuery.removeEventListener('change', this.#observeIntersection);
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();
    this.#requestVersion++;
    this.#pendingKey = '';
  }

  #requestKey() {
    const { url, productId, sectionId, intent } = this.dataset;
    return `${url}|${productId}|${sectionId}|${intent}`;
  }

  #observeQueryChanges() {
    if (!this.isConnected) return;
    this.#mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-url', 'data-product-id', 'data-section-id', 'data-intent', 'data-recommendations-performed'],
    });
  }

  /**
   * Load the product recommendations
   */
  async #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const key = this.#requestKey();
    if (this.#pendingKey === key) return;
    if (recommendationsPerformed === 'true' && this.#renderedKey === key) return;

    const requestVersion = ++this.#requestVersion;
    this.#pendingKey = key;

    try {
      if (!productId || !sectionId) {
        throw new Error('Product ID and a section ID are required');
      }

      const result = await this.#fetchCachedRecommendations(productId, sectionId, intent);
      if (!this.isConnected || requestVersion !== this.#requestVersion || key !== this.#requestKey()) return;

      if (!result.success) {
        // Visual preview can reference a section ID unavailable to the rendering API.
        if (!Shopify.designMode) throw new Error(`Server returned ${result.status}`);
        return;
      }
      if (!result.data?.trim().length) throw new Error('No recommendations available');

      this.#mutationObserver.disconnect();
      try {
        await morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
        if (!this.isConnected || requestVersion !== this.#requestVersion) return;
        this.#renderedKey = key;
        this.dataset.recommendationsPerformed = 'true';
        this.classList.remove('hidden');
        delete this.dataset.error;
      } finally {
        this.#observeQueryChanges();
      }
    } catch (error) {
      if (error?.name !== 'AbortError' && this.isConnected && requestVersion === this.#requestVersion) {
        this.#handleError(error);
      }
    } finally {
      if (requestVersion === this.#requestVersion) this.#pendingKey = '';
    }
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      const response = await fetch(url, { signal: controller.signal, priority: 'low' });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
